import type { BaseTimelineElementParams, CompElement, Reason } from '../../types';
import { ElementUpdateTypes } from '../../Enums/ElementUpdateTypes';
import { BaseElement } from './BaseElement';
import { type GroupElement } from './GroupElement';
import { CreativeTypes } from '../../Enums/CreativeTypes';
import { PreviewTypes } from '../../Enums/PreviewTypes';

export abstract class BaseTimelineElement extends BaseElement {
    hidden = false;

    startFrame = 0;

    duration = 0;

    renderOrder = 0;

    parent: GroupElement | null = null;

    allowToggleVisibility = false;

    setProperties(params: Partial<BaseTimelineElementParams>): Set<ElementUpdateTypes> {
        const updateTypes: Set<ElementUpdateTypes> = super.setProperties(params);

        if (params.hidden !== undefined && params.hidden !== this.hidden) {
            this.hidden = params.hidden;
            updateTypes.add(ElementUpdateTypes.VISIBILITY);
        }

        if (params.startFrame !== undefined && params.startFrame !== this.startFrame) {
            this.startFrame = params.startFrame;
            updateTypes.add(ElementUpdateTypes.TIMEFRAME);
        }

        if (params.duration !== undefined && params.duration !== this.duration) {
            this.duration = params.duration;
            updateTypes.add(ElementUpdateTypes.TIMEFRAME);
        }

        if (params.renderOrder !== undefined && params.renderOrder !== this.renderOrder) {
            this.renderOrder = params.renderOrder;
            updateTypes.add(ElementUpdateTypes.RENDER_ORDER);
        }

        if (params.allowToggleVisibility !== undefined) {
            this.allowToggleVisibility = params.allowToggleVisibility;
            updateTypes.add(ElementUpdateTypes.ALLOW_VISIBILITY_TOGGLE);
        }

        if ('parent' in params) {
            this.parent = params.parent;
        }

        return updateTypes;
    }

    isVisible(frameIndex: number): boolean {
        // note that frames can contain fractions (for motion-blur), so we cannot just check for 'frameIndex < this.startFrame + this.duration'
        return !this.hidden && frameIndex >= this.startFrame && frameIndex <= this.startFrame + this.duration - 1;
    }

    getCompElement(frameIndex: number): CompElement | null | undefined {
        // Override in subclass
        throw new Error('Not implemented');
    }

    getCopy(): typeof this {
        const element = super.getCopy();

        element.parent = this.parent;

        return element;
    }

    getValidationRules(creativeType: CreativeTypes, previewType: PreviewTypes): any {
        return {
            duration: {
                GREATER_THAN: 1,
                FRAMES_TO_TIME: true,
            },
        };
    }

    specificUpdateDataProcessing(
        params: Partial<BaseTimelineElementParams>,
        reason: Reason,
    ): Partial<BaseTimelineElementParams> {
        return params;
    }

    toObject(): Omit<BaseTimelineElementParams, 'parent'> {
        const baseObject = super.toObject();

        return {
            ...baseObject,
            parentId: this.parent?.id,
            hidden: this.hidden,
            startFrame: this.startFrame,
            duration: this.duration,
            renderOrder: this.renderOrder,
            allowToggleVisibility: this.allowToggleVisibility ?? false,
        };
    }
}
